/**
 * ThrottledModelQueue — cache-aware, time-spaced insertion queue.
 *
 * Processes queued tasks ONE AT A TIME via a `setTimeout` chain so a burst of
 * 3D model insertions (a zoom-in LOD reload, or a high-speed arrival burst in
 * the event planner) doesn't instantiate everything in a single frame and
 * hitch the renderer. The delay BEFORE the next task depends on whether the
 * task just run needed a network model load: tasks that hit the in-memory
 * cache (or build a procedural/synthetic model) space at `cachedDelayMs`
 * (default 100ms); tasks that fetched a model from the network space at
 * `loadDelayMs` (default 500ms).
 *
 * Tasks report this via their return value so the queue can pace itself
 * without knowing anything about the loader internals.
 */
export interface ThrottledTaskResult {
  /** True if the task triggered a (possibly async) network model load. */
  neededLoad: boolean
}

type Task = {
  id: string
  run: () => ThrottledTaskResult | Promise<ThrottledTaskResult>
}

export class ThrottledModelQueue {
  private queue: Task[] = []
  private timer: ReturnType<typeof setTimeout> | null = null
  private running = false

  constructor(
    private cachedDelayMs = 100,
    private loadDelayMs = 500
  ) {}

  /**
   * Update the inter-task spacing at runtime. Both `0` makes the queue drain
   * effectively instantly (one task per macrotask) — i.e. throttling off.
   */
  setDelays(cachedMs: number, loadMs: number): void {
    this.cachedDelayMs = cachedMs
    this.loadDelayMs = loadMs
  }

  /** Queue a task. If an item with the same id is already queued, replace it. */
  enqueue(id: string, run: Task['run']): void {
    const existing = this.queue.findIndex((t) => t.id === id)
    if (existing !== -1) this.queue[existing] = { id, run }
    else this.queue.push({ id, run })
    this.kick()
  }

  /** Drop a still-queued task. No effect once the task has started running. */
  cancel(id: string): void {
    this.queue = this.queue.filter((t) => t.id !== id)
  }

  /** Whether an id is still waiting in the queue. */
  has(id: string): boolean {
    return this.queue.some((t) => t.id === id)
  }

  get size(): number {
    return this.queue.length
  }

  /** Cancel the pending timer and drop all queued tasks (unmount cleanup). */
  clear(): void {
    if (this.timer != null) {
      clearTimeout(this.timer)
      this.timer = null
    }
    this.queue = []
    this.running = false
  }

  private kick(): void {
    if (this.running || this.timer != null) return
    void this.drainOne()
  }

  private async drainOne(): Promise<void> {
    const task = this.queue.shift()
    if (!task) {
      this.running = false
      return
    }
    this.running = true
    let neededLoad = false
    try {
      const result = await task.run()
      neededLoad = result?.neededLoad ?? false
    } catch (err) {
      console.error('ThrottledModelQueue task failed:', err)
    }
    this.running = false
    if (!this.queue.length) return
    const delay = neededLoad ? this.loadDelayMs : this.cachedDelayMs
    this.timer = setTimeout(() => {
      this.timer = null
      void this.drainOne()
    }, delay)
  }
}
